import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import { usePrudentInvestor } from './usePrudentInvestor';
import { useScreenType } from '../../hooks/useScreenType';

// Sub-components
import DesktopPrudent from './DesktopPrudent';
import MobilePrudent from './MobilePrudent';

const PrudentInvestorScreen: React.FC = () => {
    const { data, loading, error, refetch } = usePrudentInvestor();
    const navigate = useNavigate();
    const { isMobile } = useScreenType();
    const [activeTab, setActiveTab] = useState<'opportunities' | 'sell' | 'universe' | 'info'>('opportunities');
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilters, setActiveFilters] = useState<string[]>([]);

    if (loading) {
        return (
            <SiteLayout>
                <Header title="Investisseur Prudent" />
                <Box>
                    <div style={{ padding: 40, textAlign: 'center' }}>
                        <div className="loading-spinner" />
                        <p>Recherche d'opportunités (Stochastique)...</p>
                    </div>
                </Box>
            </SiteLayout>
        );
    }

    if (error) {
        return (
            <SiteLayout>
                <Header title="Investisseur Prudent" />
                <Box>
                    <div style={{ padding: 40, textAlign: 'center', color: '#dc2626' }}>
                        <p>Erreur: {error}</p>
                        <button 
                            onClick={refetch} 
                            style={{ 
                                marginTop: 16, 
                                padding: '10px 20px', 
                                backgroundColor: '#2563eb', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: 6, 
                                cursor: 'pointer' 
                            }}
                        >
                            Réessayer
                        </button>
                    </div>
                </Box>
            </SiteLayout>
        );
    }

    const opportunities = data?.opportunities || [];
    const sellSignals = data?.sell_signals || [];
    const eligibleUniverse = data?.eligible_universe || [];
    const sentiment = data?.market_sentiment || 'NEUTRAL';

    const toggleFilter = (filter: string) => {
        setActiveFilters(prev => prev.includes(filter) ? prev.filter(f => f !== filter) : [...prev, filter]);
    };

    const filteredUniverse = eligibleUniverse.filter(stock => {
        const matchesSearch = (stock.nom_societe?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (stock.symbole?.toLowerCase() || '').includes(searchTerm.toLowerCase());

        if (!matchesSearch) return false;

        if (activeFilters.includes('rsi70') && (stock.rsi || 100) >= 70) return false;
        if (activeFilters.includes('stoch20') && (stock.stoch_k || 100) >= 20) return false;
        if (activeFilters.includes('liquid') && (stock.avg_turnover || 0) < 2000000) return false;
        if (activeFilters.includes('undervalued') && !stock.is_undervalued) return false;
        if (activeFilters.includes('obvBullish') && stock.obv_trend !== 'BULLISH') return false;
        if (activeFilters.includes('goldenCross') && !stock.is_golden_cross) return false;

        return true;
    });

    const sentimentColors: Record<string, { bg: string; color: string }> = {
        BULLISH: { bg: '#dcfce7', color: '#16a34a' },
        BEARISH: { bg: '#fee2e2', color: '#dc2626' },
        NEUTRAL: { bg: '#f1f5f9', color: '#475569' },
    };

    const childProps = {
        data,
        activeTab,
        setActiveTab,
        searchTerm,
        setSearchTerm,
        activeFilters,
        toggleFilter,
        opportunities,
        sellSignals,
        filteredUniverse,
        sentiment,
        sentimentColors,
        navigate: (path: string) => navigate(path)
    };

    return (
        <SiteLayout>
            <Header title="Investisseur Prudent" />
            {isMobile ? (
                <MobilePrudent {...childProps} />
            ) : (
                <DesktopPrudent {...childProps} />
            )}
        </SiteLayout>
    );
};

export default PrudentInvestorScreen;
